import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCms } from '../cms/CmsContext';
import { Eye, EyeOff } from 'lucide-react';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import './SignupPage.css';
import { getCmsImagePath } from '../cms/cmsData';
import { getArabicTranslation } from '../utils/arabicTranslations';

// Sanitize redirect param to prevent open redirect / XSS
const sanitizeRedirect = (redirect) => {
  if (!redirect || typeof redirect !== 'string') return '/dashboard';
  const trimmed = redirect.trim();
  // Only allow relative paths starting with a single /
  // Block: //, http:, https:, javascript:, data:, vbscript:, or any scheme
  if (
    trimmed.startsWith('//') ||
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) ||
    !trimmed.startsWith('/')
  ) {
    return '/dashboard';
  }
  return trimmed;
};

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated } = useAuth();
  const { content } = useCms();
  const cms = content?.signupPage || {};
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    confirmEmail: '',
    password: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      // Check if user came from checkout
      const searchParams = new URLSearchParams(location.search);
      const redirect = searchParams.get('redirect');
      navigate(sanitizeRedirect(redirect));
    }
  }, [isAuthenticated, navigate, location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;
    
    if (formData.email.trim().toLowerCase() !== formData.confirmEmail.trim().toLowerCase()) {
      setError('Emails do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await register(
        formData.email,
        formData.password,
        formData.password,
        formData.fullName,
        formData.phone,
        smsConsent,
        formData.confirmEmail
      );
      
      if (result?.success) {
        // Check if user came from checkout
        const searchParams = new URLSearchParams(location.search);
        const redirect = searchParams.get('redirect');
        navigate(sanitizeRedirect(redirect));
      } else {
        setError(result?.error?.message || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper signup-page">
      <AnnouncementBar />
      
      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="card-title" style={{ paddingBottom: '30px' }}>{cms.title} <span className="arabic-text on-white">{getArabicTranslation('Register')}</span></h1>
          
          <div className="auth-options" style={{ paddingBottom: '30px' }}>
            <Link to={`/login${location.search}`} className="auth-option" title="Login">
              {cms.loginTab}
            </Link>
            <Link to={`/signup${location.search}`} className="auth-option active" title="Register">
              {cms.registerTab}
              <div className="active-indicator"></div>
            </Link>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <div className="email-input-wrapper">
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">{cms.emailLabel}</label>
              <div className="email-input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmEmail">Confirm Email</label>
              <div className="email-input-wrapper">
                <input
                  type="email"
                  id="confirmEmail"
                  name="confirmEmail"
                  value={formData.confirmEmail}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <div className="email-input-wrapper">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
              </div>
            </div>

            <div className="form-group password-group">
              <label htmlFor="password">{cms.passwordLabel}</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder=""
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-consent">
              <p className="consent-text"><strong>OPTIONAL:</strong><br/>I wish to receive transactional SMS messages from OC MENA Festival regarding orders (not required for purchase). By providing your mobile number and checking this box, you agree to receive transactional SMS messages from OC MENA Festival related to your order. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help. View our <Link to="/privacy-policy" className="privacy-link" style={{ color: '#dc2626', textDecoration: 'underline' }}>Privacy Policy</Link> and <Link to="/terms-of-use" className="privacy-link" style={{ color: '#dc2626', textDecoration: 'underline' }}>Terms</Link>.</p>
              <label className="checkbox-label" style={{ paddingLeft: '28px' }} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <span>I agree to receive transactional SMS messages from OC MENA Festival regarding orders.</span>
              </label>
            </div>

            {error && <div className="error-message" style={{color: '#e74c3c', marginBottom: '1rem', textAlign: 'center'}}>{error}</div>}

            <div className="privacy-notice">
              <p>
                {cms.privacyNotice.split('privacy policy')[0]}<Link to="/privacy-policy" className="privacy-link">privacy policy</Link> and <Link to="/terms-of-use" className="privacy-link">terms of use</Link>.
              </p>
            </div>
            <br/>

            <button type="submit" className="btn-primary submit-btn" disabled={loading}>
              {loading ? cms.submittingButton : cms.submitButton}
            </button>
          </form>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath("lanterns", "/lanterns.webp")} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      {/* Sponsors Section */}
      <SponsorsSection />

      {/* Footer */}
      <Footer />
      
      <ScrollToTop />
    </div>
  );
};

export default SignupPage;
